/**
 * CommandPalette
 *
 * Universal searchable command palette for page actions.
 *
 * Desktop: centered floating panel (like VS Code's Ctrl+P).
 *   - Fade + scale animation
 *   - Keyboard focus trap (Tab cycles, Escape closes)
 *   - Arrow key navigation, Enter to execute
 *
 * Mobile: full-screen bottom sheet drawer.
 *   - Drag handle at top
 *   - Swipe down to close
 *   - Overscroll resistance via CSS
 *   - Dimmed + blurred backdrop
 *   - 44px minimum touch targets
 *
 * Triggers:
 *   - Keyboard shortcut: Ctrl+K / Cmd+K
 *   - Toolbar overflow button
 *   - Programmatic via `open` prop
 *
 * Features:
 *   - Fuzzy search across action labels, descriptions, and groups
 *   - Grouped by category/group with ARIA grouping
 *   - Plugin icons alongside actions
 *   - Screen reader-friendly with aria-live announcements
 *   - Lazy rendering: content only mounts when open
 *
 * No domain logic — pure infrastructure.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePageActionContext } from './PageActionProvider';
import { usePageActions } from '../../plugins/registries/page-action-registry';
import { useDeviceContext } from '../../providers/DeviceTypeProvider';
import { useFocusTrap, useAriaLive, usePrefersReducedMotion } from '../../hooks/useAccessibility';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import type { PageAction } from '../../types/page-actions';
import { Input, Text, HStack, Stack } from '@neuro/design-system';
import { ActionIcon } from './ActionIcon';
import { useOverlayCommandRegistry } from '../../ui/commands/command-registry';
import type { CommandDefinition as OverlayCommand } from '../../ui/commands/command-registry';
import {
  pageActionToUnified,
  overlayCommandToUnified,
  filterUnifiedCommands,
  groupUnifiedCommands,
  type UnifiedCommand,
  type UnifiedGroup,
} from './unified-command';
import { actionResultBridge } from '../../plugins/action-result-bridge';

// ── Unified commands migrated to unified-command.ts ──────────────

// ── Destructive confirm helper ──────────────────────────────────

async function confirmDestructive(cmd: { label: string }): Promise<boolean> {
  return window.confirm(
    `Are you sure you want to "${cmd.label}"? This action cannot be undone.`,
  );
}

// ── Component ───────────────────────────────────────────────────

export interface CommandPaletteProps {
  /** Whether the palette is currently open */
  open?: boolean;
  /** Callback when the palette should close */
  onClose?: () => void;
  /** Additional CSS class */
  className?: string;
}

export function CommandPalette({
  open: controlledOpen,
  onClose,
  className,
}: CommandPaletteProps) {
  const context = usePageActionContext();
  const { isMobile } = useDeviceContext();
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [executing, setExecuting] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const announce = useAriaLive();

  const isOpen = controlledOpen ?? internalOpen;

  // Get both page actions and overlay commands
  const pageActions = usePageActions(context, 'command-palette');
  // Get the raw commands Set from the registry
  const commandsSet = useOverlayCommandRegistry((s) => s.commands);
  
  // Stabilize overlayCommands reference using useMemo to prevent infinite dependency updates
  const overlayCommands = useMemo(() => Array.from(commandsSet.values()), [commandsSet]);

  // Focus trap for desktop dialog
  const focusTrapRef = useFocusTrap<HTMLDivElement>(isOpen && !isMobile, {
    autoFocus: false,
    escapeDeactivates: true,
    onEscape: () => handleClose(),
  });

  // Swipe-down to close on mobile
  const swipeRef = useSwipeGesture<HTMLDivElement>({
    directions: ['down'],
    threshold: 60,
    velocityThreshold: 0.2,
    maxDuration: 400,
    enabled: isOpen && isMobile,
    onSwipe: (e) => {
      if (e.direction === 'down') {
        handleClose();
      }
    },
  });

  const sheetRef = useRef<HTMLDivElement>(null);

  // Merge page actions and overlay commands into unified list
  const allUnified = useMemo<UnifiedCommand[]>(() => {
    const unified: UnifiedCommand[] = [];

    // Convert page actions
    for (const action of pageActions) {
      unified.push(pageActionToUnified(action, context, 0));
    }

    // Convert overlay commands
    for (const cmd of overlayCommands) {
      if (cmd.isAvailable && !cmd.isAvailable(context)) continue;
      unified.push(overlayCommandToUnified(cmd, context, 0));
    }

    return unified;
  }, [pageActions, overlayCommands]);

  // Filter, score, and sort by search query
  const filteredCommands = useMemo(
    () => filterUnifiedCommands(allUnified, search),
    [allUnified, search],
  );

  // Group the filtered commands
  const groups = useMemo(() => groupUnifiedCommands(filteredCommands), [filteredCommands]);

  // Flat list for keyboard navigation
  const flatCommands = useMemo(
    () => groups.flatMap((g) => g.commands),
    [groups],
  );

  // Announce result count to screen readers
  useEffect(() => {
    if (isOpen && search) {
      announce(`${filteredCommands.length} action${filteredCommands.length !== 1 ? 's' : ''} found`);
    }
  }, [filteredCommands.length, isOpen, search, announce]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      setClosing(false);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Global keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          handleClose();
        } else {
          setInternalOpen(true);
        }
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        handleClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (prefersReducedMotion) {
      setInternalOpen(false);
      onClose?.();
      return;
    }
    setClosing(true);
    const closeDelay = isMobile ? 200 : 150;
    setTimeout(() => {
      setInternalOpen(false);
      setClosing(false);
      onClose?.();
    }, closeDelay);
  }, [onClose, prefersReducedMotion, isMobile]);

  // Execute a unified command (with confirmation for destructive)
  const execute = useCallback(
    async (cmd: UnifiedCommand) => {
      if (cmd.disabled) return;
      if (cmd.destructive) {
        const ok = await confirmDestructive(cmd);
        if (!ok) return;
      }
      setExecuting(cmd.id);
      try {
        await cmd.execute(context);
        actionResultBridge.success('Action completed', cmd.label, cmd.source);
      } catch (err) {
        console.error(`[CommandPalette] Unified command "${cmd.id}" failed:`, err);
        actionResultBridge.error(
          'Action failed',
          err instanceof Error ? err.message : String(err),
          cmd.source,
        );
      } finally {
        setExecuting(null);
        handleClose();
      }
    },
    [context, handleClose],
  );

  // Keyboard navigation within the palette
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, flatCommands.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatCommands[selectedIndex]) {
            execute(flatCommands[selectedIndex]);
          }
          break;
        case 'Home':
          e.preventDefault();
          setSelectedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setSelectedIndex(Math.max(flatCommands.length - 1, 0));
          break;
      }
    },
    [flatCommands, selectedIndex, execute],
  );

  // Scroll selected item into view
  useEffect(() => {
    const selected = listRef.current?.querySelector('[data-selected="true"]');
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen && !closing) return null;

  // Determine layout mode
  const isMobileLayout = isMobile;
  let flatIndex = -1;

  const closingClass = closing ? 'command-palette--closing' : '';
  const reducedMotionClass = prefersReducedMotion ? 'command-palette--reduced-motion' : '';

  // Shared content renderer
  const renderContent = () => (
    <>
      {/* Search input */}
      <div className="command-palette__search">
        <Input
          ref={inputRef}
          placeholder="Search actions…"
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          inputSize="md"
          className="command-palette__input"
          role="combobox"
          aria-expanded={true}
          aria-controls="command-palette-listbox"
          aria-activedescendant={
            flatCommands[selectedIndex]
              ? `command-palette-item-${flatCommands[selectedIndex].id}`
              : undefined
          }
          aria-autocomplete="list"
          aria-label="Search actions"
        />
      </div>

      {/* Action list */}
      <div
        className="command-palette__list"
        ref={listRef}
        role="listbox"
        id="command-palette-listbox"
        aria-label="Available actions"
      >
        {flatCommands.length === 0 && (
          <div className="command-palette__empty" role="status">
            <Text size="sm" color="muted">
              {search ? 'No matching actions' : 'No actions available'}
            </Text>
          </div>
        )}

        {groups.map((group) => (
          <div
            key={group.key}
            className="command-palette__group"
            role="group"
            aria-labelledby={`command-palette-group-${group.key}`}
          >
            <div
              className="command-palette__group-header"
              id={`command-palette-group-${group.key}`}
            >
              <Text size="xs" color="muted" weight="semibold">
                {group.label}
              </Text>
            </div>

            {group.commands.map((cmd) => {
              flatIndex++;
              const isSelected = flatIndex === selectedIndex;

              return (
                <button
                  key={cmd.id}
                  id={`command-palette-item-${cmd.id}`}
                  role="option"
                  aria-selected={isSelected}
                  data-selected={isSelected}
                  className={[
                    'command-palette__item',
                    isSelected && 'command-palette__item--selected',
                    cmd.destructive && 'command-palette__item--destructive',
                    cmd.disabled && 'command-palette__item--disabled',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={cmd.disabled || executing === cmd.id}
                  onClick={() => execute(cmd)}
                  aria-label={`${cmd.label}${cmd.description ? ` — ${cmd.description}` : ''}`}
                >
                  <span className="command-palette__item-icon">
                    <ActionIcon icon={cmd.icon} size={16} />
                  </span>

                  <span className="command-palette__item-content">
                    <span className="command-palette__item-label">
                      {executing === cmd.id ? 'Working…' : cmd.label}
                    </span>
                    {cmd.description && (
                      <span className="command-palette__item-description">
                        <Text size="xs" color="muted">
                          {cmd.description}
                        </Text>
                      </span>
                    )}
                  </span>

                  {cmd.shortcut && (
                    <span className="command-palette__item-shortcut">
                      <kbd>{cmd.shortcut}</kbd>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer hint (hidden on mobile) */}
      {!isMobileLayout && (
        <div className="command-palette__footer">
          <HStack gap={12}>
            <Text size="xs" color="muted">
              <kbd>↑↓</kbd> Navigate
            </Text>
            <Text size="xs" color="muted">
              <kbd>↵</kbd> Execute
            </Text>
            <Text size="xs" color="muted">
              <kbd>Esc</kbd> Close
            </Text>
          </HStack>
        </div>
      )}
    </>
  );

  // ── Mobile: bottom-sheet drawer ───────────────────────────────
  if (isMobileLayout) {
    return (
      <div
        className={`command-palette command-palette--mobile ${closingClass} ${reducedMotionClass} ${className ?? ''}`}
        onKeyDown={handleKeyDown}
      >
        <div
          className={`command-palette__backdrop ${closing ? 'command-palette__backdrop--out' : ''}`}
          onClick={handleClose}
        />
        <div
          ref={(node) => {
            // Merge swipe ref and sheet ref
            (swipeRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            (sheetRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
          className={`command-palette__sheet ${closing ? 'command-palette__sheet--out' : ''}`}
          role="dialog"
          aria-label="Command Palette"
          aria-modal="true"
        >
          {/* Drag handle indicator */}
          <div className="command-palette__sheet-handle" aria-hidden="true">
            <div className="command-palette__sheet-handle-bar" />
          </div>
          {renderContent()}
        </div>
      </div>
    );
  }

  // ── Desktop: centered floating panel ──────────────────────────
  return (
    <div
      className={`command-palette command-palette--desktop ${closingClass} ${reducedMotionClass} ${className ?? ''}`}
      onKeyDown={handleKeyDown}
    >
      <div
        className={`command-palette__backdrop ${closing ? 'command-palette__backdrop--out' : ''}`}
        onClick={handleClose}
      />
      <div
        ref={focusTrapRef}
        className={`command-palette__panel ${closing ? 'command-palette__panel--out' : ''}`}
        role="dialog"
        aria-label="Command Palette"
        aria-modal="true"
      >
        {renderContent()}
      </div>
    </div>
  );
}
